import { updateOrderWithPayment } from "../services/orderService";
import {
  clearFavouriteArtSavedLocally,
  isFavouriteArtSavedLocally,
} from "../services/favouritesService";
import {
  clearPendingPaymentOrder,
  hasPendingPaymentOrder,
  readPendingPaymentOrder,
} from "./pendingPaymentOrder";

export const LATEST_ORDER_ART_IMAGE_KEY = "latestOrderArtImageUrl";

/** Persist the image the user most recently chose for print / order attachment. */
export function persistLatestChosenArtUrl(imageUrl: string): void {
  const trimmed = imageUrl?.trim();
  if (!trimmed) return;

  try {
    localStorage.setItem(LATEST_ORDER_ART_IMAGE_KEY, trimmed);
    localStorage.setItem("favouriteImg", trimmed);
    // New art that isn't marked saved yet → reset local isAlreadySaved to false.
    if (!isFavouriteArtSavedLocally(trimmed)) {
      clearFavouriteArtSavedLocally();
    }
  } catch {
    /* ignore quota errors */
  }
}

export function readLatestChosenArtUrl(): string | null {
  try {
    const fromLatest = localStorage.getItem(LATEST_ORDER_ART_IMAGE_KEY)?.trim();
    if (fromLatest) return fromLatest;
    const fromFavourite = localStorage.getItem("favouriteImg")?.trim();
    return fromFavourite || null;
  } catch {
    return null;
  }
}

export interface FinalizeOrderOptions {
  /** Keep orderId so the same paid order can be updated again (e.g. try-again flow). */
  retainOrder?: boolean;
}

/**
 * After QR payment socket returns orderId, PATCH /product/order/{orderId}/with-payment
 * with generated art when the user accepts the result.
 */
export async function finalizeOrderWithGeneratedArt(
  imageUrl: string,
  options?: FinalizeOrderOptions
): Promise<{ success: boolean; skipped?: boolean; error?: string }> {
  const trimmedImageUrl = imageUrl?.trim();
  if (!trimmedImageUrl) {
    return { success: false, error: "Generated image is missing." };
  }

  persistLatestChosenArtUrl(trimmedImageUrl);

  if (!hasPendingPaymentOrder()) {
    return { success: true, skipped: true };
  }

  const orderId = readPendingPaymentOrder();
  if (!orderId) {
    return {
      success: false,
      error: "Payment order is missing. Complete QR payment first.",
    };
  }

  try {
    const response = await updateOrderWithPayment(orderId, {
      orderId,
      image_url: trimmedImageUrl,
    });

    if (!response.success) {
      return {
        success: false,
        error: response.error || "Failed to update order with generated art.",
      };
    }

    if (!options?.retainOrder) {
      clearPendingPaymentOrder();
    }
    return { success: true };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : "Failed to finalize order.";
    console.error("finalizeOrderWithGeneratedArt:", error);
    return { success: false, error: message };
  }
}
